import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { initializeData } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function Login() {
  const { login, register, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  useEffect(() => {
    if (token) {
      navigate('/erp/dashboard');
    }
    // Initialize default data on first load
    initializeData().catch(() => {});
  }, [token, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Bienvenido a PICHIROCES AMAZONICOS');
      navigate('/erp/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm);
      toast.success('Cuenta creada exitosamente');
      navigate('/erp/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1653149875526-e2533c6af095?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      />
      <div className="absolute inset-0 bg-emerald-950/70" />

      {/* Login Card */}
      <Card className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img
              src="/logo-small.png"
              alt="Pichiroces Amazónicos"
              className="w-20 h-20 rounded-full object-cover mx-auto"
              data-testid="login-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <CardTitle className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            PICHIROCES AMAZONICOS
          </CardTitle>
          <CardDescription className="text-stone-600">
            Sistema de Gestión de Fraternidad
          </CardDescription>
          <a href="/" className="text-sm text-emerald-700 hover:text-emerald-900 hover:underline mt-2 inline-block" data-testid="back-to-home-link">
            &larr; Volver al inicio
          </a>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-stone-800 font-medium">Correo Electrónico</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email"
                    className="h-11 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-stone-800 font-medium">Contraseña</Label>
                  <div className="flex items-center border border-stone-300 rounded-md focus-within:ring-1 focus-within:ring-ring bg-white">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      autoComplete="current-password"
                      data-testid="login-password"
                      className="h-11 border-0 focus-visible:ring-0 shadow-none flex-1 bg-transparent text-stone-900 placeholder:text-stone-400"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword(!showPassword)}
                      className="px-3 text-stone-500 hover:text-stone-700 shrink-0"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-emerald-900 hover:bg-emerald-800"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-stone-800 font-medium">Nombre Completo</Label>
                  <Input
                    id="register-name"
                    type="text"
                    placeholder="Juan Pérez"
                    value={registerForm.full_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                    required
                    data-testid="register-name"
                    className="h-11 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-stone-800 font-medium">Correo Electrónico</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    data-testid="register-email"
                    className="h-11 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-phone" className="text-stone-800 font-medium">Teléfono (opcional)</Label>
                  <Input
                    id="register-phone"
                    type="tel"
                    placeholder="+591 70000000"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                    data-testid="register-phone"
                    className="h-11 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-stone-800 font-medium">Contraseña</Label>
                  <div className="flex items-center border border-stone-300 rounded-md focus-within:ring-1 focus-within:ring-ring bg-white">
                    <Input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      data-testid="register-password"
                      className="h-11 border-0 focus-visible:ring-0 shadow-none flex-1 bg-transparent text-stone-900 placeholder:text-stone-400"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword(!showPassword)}
                      className="px-3 text-stone-500 hover:text-stone-700 shrink-0"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-emerald-900 hover:bg-emerald-800"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    'Crear Cuenta'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
